import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User, Bot, ShoppingBag, Sparkles, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/card';
import { Input } from './ui/input';
import { chatApi } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { ScrollArea } from './ui/scroll-area';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useApp();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (user && isOpen && !sessionId) {
      loadMySession();
    }
  }, [user, isOpen, sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  const loadMySession = async () => {
    try {
      const { data } = await chatApi.getMySession();
      if (data) {
        setSessionId(data.id);
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
    }
  };

  const startSession = async () => {
    try {
      const { data } = await chatApi.createSession();
      setSessionId(data.id);
      setMessages(data.messages);
      return data.id;
    } catch (error) {
      console.error('Error starting chat session:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || !user) return;

    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = await startSession();
    }

    const userMessage = { sender: 'user', message: message.trim() };
    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);

    try {
      const { data } = await chatApi.sendMessage(currentSessionId, userMessage);
      if (data.sender === 'ai') {
        setMessages(prev => [...prev, data]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessageContent = (msg) => {
    const parts = msg.message.split(/(\[\[.*?\]\])/g);
    
    return (
      <div className="space-y-2">
        <p className="leading-relaxed">
          {parts.map((part, i) => {
            if (part.startsWith('[[') && part.endsWith(']]')) {
              const slug = part.slice(2, -2);
              return (
                <Link 
                  key={i} 
                  to={`/products/${slug}`} 
                  className="mx-1 px-2 py-0.5 rounded-md bg-primary/20 text-primary font-bold hover:bg-primary/30 transition-colors inline-flex items-center gap-1.5 no-underline"
                >
                  <ShoppingBag className="h-3.5 w-3.5" />
                  View Details
                </Link>
              );
            }
            return part;
          })}
        </p>
      </div>
    );
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      <AnimatePresence>
        {!isOpen ? (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Button 
              onClick={() => setIsOpen(true)}
              className="rounded-full h-16 w-16 shadow-[0_8px_30px_rgb(139,92,246,0.3)] bg-gradient-to-tr from-primary to-accent border-none flex items-center justify-center p-0 group"
            >
              <MessageCircle className="h-7 w-7 text-white group-hover:rotate-12 transition-transform duration-300" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20, transformOrigin: 'bottom right' }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="w-[360px] sm:w-[420px]"
          >
            <Card className="h-[550px] flex flex-col shadow-2xl border-white/10 bg-background/80 backdrop-blur-xl overflow-hidden rounded-3xl border">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary via-primary/90 to-accent p-6 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Sparkles className="h-20 w-20 rotate-12" />
                </div>
                <div className="flex flex-row items-center justify-between relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                      <Bot className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg tracking-tight">Ved3D Assistant</h3>
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-xs text-white/80 font-medium">Always Online</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setIsOpen(false)} 
                    className="h-9 w-9 rounded-xl hover:bg-white/20 text-white transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <CardContent className="flex-1 p-0 overflow-hidden bg-dot-pattern">
                {!user ? (
                  <div className="h-full flex flex-col items-center justify-center p-8 text-center space-y-6">
                    <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center">
                      <LogIn className="h-10 w-10 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-xl font-bold">Join the conversation</h4>
                      <p className="text-sm text-muted-foreground">Log in to chat with our AI expert and get personalized assistance.</p>
                    </div>
                    <Button asChild className="w-full rounded-2xl h-12 text-md font-semibold shadow-lg shadow-primary/20">
                      <Link to="/login" onClick={() => setIsOpen(false)}>Log In Now</Link>
                    </Button>
                  </div>
                ) : (
                  <ScrollArea className="h-full px-4 pt-6 pb-4">
                    <div className="space-y-6">
                      {messages.length === 0 && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="py-6 px-4"
                        >
                          <div className="bg-primary/5 rounded-2xl p-5 border border-primary/10 space-y-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Sparkles className="h-5 w-5 text-primary" />
                              </div>
                              <h4 className="font-bold text-foreground">Welcome to Ved3D Assistant!</h4>
                            </div>
                            
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground font-medium">How you can use this bot:</p>
                              <ul className="space-y-2 text-sm text-foreground/70">
                                <li className="flex gap-2">
                                  <span className="text-primary font-bold">•</span>
                                  <span>Ask about our 3D printed products and designs</span>
                                </li>
                                <li className="flex gap-2">
                                  <span className="text-primary font-bold">•</span>
                                  <span>Inquire about custom 3D printing orders</span>
                                </li>
                                <li className="flex gap-2">
                                  <span className="text-primary font-bold">•</span>
                                  <span>Check your order status or account features</span>
                                </li>
                              </ul>
                            </div>

                            <p className="text-[12px] text-muted-foreground bg-white/50 p-3 rounded-xl border border-dashed border-primary/20">
                              <strong>Note:</strong> I only answer website-related queries. If you need human help, just leave your message here and our admin will get back to you!
                            </p>
                          </div>
                        </motion.div>
                      )}
                      
                      {messages.map((msg, i) => (
                        <motion.div 
                          key={i}
                          initial={{ opacity: 0, x: msg.sender === 'user' ? 20 : -20, y: 10 }}
                          animate={{ opacity: 1, x: 0, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                          className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex gap-3 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div className={`h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                              msg.sender === 'user' ? 'bg-primary' : 'bg-secondary border border-primary/10'
                            }`}>
                              {msg.sender === 'user' ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4" />}
                            </div>
                            <div className={`p-4 rounded-2xl shadow-sm text-[13.5px] ${
                              msg.sender === 'user' 
                              ? 'bg-primary text-white rounded-tr-none' 
                              : 'bg-card border border-white/5 rounded-tl-none text-card-foreground'
                            }`}>
                              {renderMessageContent(msg)}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="flex gap-3 max-w-[80%] flex-row">
                            <div className="h-8 w-8 rounded-xl bg-secondary flex items-center justify-center border border-primary/10">
                              <Bot className="h-4 w-4" />
                            </div>
                            <div className="p-4 rounded-2xl rounded-tl-none bg-card border border-white/5 shadow-sm">
                              <div className="flex gap-1.5">
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={scrollRef} className="h-2" />
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
              
              {user && (
                <CardFooter className="p-4 border-t bg-background/50 backdrop-blur-md">
                  <form onSubmit={handleSend} className="flex w-full gap-2 relative">
                    <Input
                      placeholder="Ask something..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="flex-1 rounded-2xl h-12 px-5 bg-secondary/50 border-none focus-visible:ring-1 focus-visible:ring-primary/30 transition-all"
                      disabled={isLoading}
                    />
                    <Button 
                      type="submit" 
                      size="icon" 
                      disabled={!message.trim() || isLoading}
                      className="h-12 w-12 rounded-2xl shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90 transition-all"
                    >
                      <Send className="h-5 w-5" />
                    </Button>
                  </form>
                </CardFooter>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatWidget;
